// EXR Header
// Each layer in an EXR file has a header containing attributes.
// Headers are written as a sequence of attributes followed by a null byte.

import { calculateTotalTileCount } from '../block/index.js'
import { DEFAULT_TILE_SIZE, LayerType } from '../core/constants.js'
import {
  Blocks,
  Compression,
  IntegerBounds,
  LineOrder,
  scanLinesPerBlock,
  Vec2,
} from '../core/types.js'
import {
  writeBox2i,
  writeChannelList,
  writeCompression,
  writeFloat,
  writeInt,
  writeLineOrder,
  writeString,
  writeTileDescription,
  writeType,
  writeV2f,
} from './attributes.js'

// Image-level attributes shared across all layers
export class ImageAttributes {
  constructor(displayWindow) {
    this.displayWindow = displayWindow
    this.pixelAspect = 1.0
    this.chromaticities = null
    this.timeCode = null
    this.custom = new Map()
  }

  // Create image attributes with display window matching a size
  static withSize(size) {
    return new ImageAttributes(IntegerBounds.fromDimensions(size.x, size.y))
  }
}

// Layer-level attributes
export class LayerAttributes {
  constructor() {
    this.layerName = null
    this.layerPosition = new Vec2(0, 0)
    this.screenWindowCenter = new Vec2(0.0, 0.0)
    this.screenWindowWidth = 1.0
    this.custom = new Map()
  }

  // Create layer attributes with a name
  static named(name) {
    const attrs = new LayerAttributes()
    attrs.layerName = name
    return attrs
  }
}

// Encoding settings for a layer
export class Encoding {
  constructor(compression, blocks, lineOrder) {
    this.compression = compression
    this.blocks = blocks
    this.lineOrder = lineOrder
  }

  // Uncompressed scanlines
  static UNCOMPRESSED = new Encoding(
    Compression.Uncompressed,
    Blocks.ScanLines,
    LineOrder.Increasing,
  )

  // RLE compressed tiles - fast encoding, good compression
  static FAST_LOSSLESS = new Encoding(
    Compression.RLE,
    Blocks.Tiles(new Vec2(DEFAULT_TILE_SIZE, DEFAULT_TILE_SIZE)),
    LineOrder.Unspecified,
  )

  // ZIP16 compressed scanlines - smaller files
  static SMALL_LOSSLESS = new Encoding(
    Compression.ZIP16,
    Blocks.ScanLines,
    LineOrder.Increasing,
  )

  // PIZ compressed tiles - best for noisy images
  static SMALL_FAST_LOSSLESS = new Encoding(
    Compression.PIZ,
    Blocks.Tiles(new Vec2(256, 256)),
    LineOrder.Unspecified,
  )
}

// Header for a single layer
export class Header {
  constructor(layerSize, channels, encoding, sharedAttributes, ownAttributes) {
    this.layerSize = layerSize
    this.channels = channels
    this.encoding = encoding
    this.sharedAttributes = sharedAttributes
    this.ownAttributes = ownAttributes
  }

  // Get the data window bounds
  get dataWindow() {
    return new IntegerBounds(this.ownAttributes.layerPosition, this.layerSize)
  }

  // Calculate the number of blocks/chunks in this layer
  get chunkCount() {
    if (this.encoding.blocks.isTiled()) {
      return calculateTotalTileCount(this.layerSize, this.encoding.blocks)
    } else {
      // Scanline blocks
      const linesPerBlock = scanLinesPerBlock(this.encoding.compression)
      return Math.ceil(this.layerSize.y / linesPerBlock)
    }
  }

  // Get the layer type string
  get layerType() {
    return this.encoding.blocks.isTiled() ? LayerType.TILED : LayerType.SCANLINE
  }

  // Write this header to a writer
  write(writer, isMultiPart) {
    // Required attributes
    writeChannelList(writer, this.channels)
    writeCompression(writer, this.encoding.compression)
    writeBox2i(writer, 'dataWindow', this.dataWindow)
    writeBox2i(writer, 'displayWindow', this.sharedAttributes.displayWindow)
    writeLineOrder(writer, this.encoding.lineOrder)
    writeFloat(writer, 'pixelAspectRatio', this.sharedAttributes.pixelAspect)
    writeV2f(
      writer,
      'screenWindowCenter',
      this.ownAttributes.screenWindowCenter,
    )
    writeFloat(
      writer,
      'screenWindowWidth',
      this.ownAttributes.screenWindowWidth,
    )

    // Tile description (if tiled)
    if (this.encoding.blocks.isTiled()) {
      writeTileDescription(
        writer,
        this.encoding.blocks.tileSize,
        this.encoding.blocks.levelMode,
        this.encoding.blocks.roundingMode,
      )
    }

    // Multi-part attributes
    if (isMultiPart) {
      // name is required for multi-part files
      const name = this.ownAttributes.layerName || ''
      writeString(writer, 'name', name)
      writeType(writer, this.layerType)
      writeInt(writer, 'chunkCount', this.chunkCount)
    }

    // Custom attributes
    for (const [_name, _value] of this.ownAttributes.custom) {
      // TODO: Support custom attribute types
    }

    // End of header (null byte)
    writer.writeU8(0)
  }
}

// Write all headers to a writer
export function writeHeaders(writer, headers, isMultiPart) {
  for (const header of headers) {
    header.write(writer, isMultiPart)
  }

  // Multi-part files have an extra null byte after all headers
  if (isMultiPart) {
    writer.writeU8(0)
  }
}
